import { ContentBox } from "@msuite/ui/app-shell";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { LazyFallback } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { Tabs } from "@msuite/ui/tabs";
import { responseHandler } from "@msuite/ui/utils";
import { getPartnershipRole } from "@msuite/utils/partnerships";
import { lazy, Suspense } from "react";
import { useParams } from "react-router";
import { OrganizationHeader } from "@/components/organizations";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

const PartnerGeneral = lazy(() => import("./partner-general"));
const PartnerTimeline = lazy(() => import("./partner-timeline"));
const PartnerAgreements = lazy(() => import("./partner-agreements"));

export function Partner() {
	/** Hooks */
	const params = useParams();
	const partnerId = params.partnerId;
	const { t } = useTranslation();
	const { organization } = useAuth();

	/** Query */
	const { data } = useQuery({
		// Named for what it holds: the query cache is persisted per user, and a key of the bare
		// organisation id would read back whatever an older page shape stored under it.
		queryKey: ["organization", partnerId ?? "", "partnerships"],
		queryFn: async () => {
			if (!partnerId) throw new Error("Partner ID is required");
			const response = await client.organizations[":orgId"].$get({
				param: {
					orgId: partnerId,
				},
			});
			const data = await responseHandler(response);
			if (!data.success) throw new Error("Failed to fetch organization data");
			return {
				organization: data.organization,
				partnerships: data.partnerships,
			};
		},
		enabled: !!partnerId,
	});

	/** Computed */
	// Between two organisations there is a row per direction. The tabs follow the row the viewer
	// hosts, since that is the one whose agreements, reports and notes the viewer keeps; a
	// counterpart that only hosts the viewer has its row followed instead.
	const partnerships = data?.partnerships ?? [];
	const organizationName = data?.organization?.name ?? "";
	const primaryPartnership =
		partnerships.find((row) => getPartnershipRole(organization.id, row) === "host") ??
		partnerships.at(0);

	/** Guard */
	if (!partnerId) return <BlankSlate />;
	if (!primaryPartnership) return <BlankSlate />;
	if (!data?.organization) return <BlankSlate />;

	/** Render */
	return (
		<ContentBox bottomPadding="none">
			<OrganizationHeader
				organization={data.organization}
				partnerships={partnerships}
			/>
			<Tabs
				panelKey="partner-tabs"
				groups={[
					{
						name: "general",
						panels: [
							{
								label: t("general.general"),
								errors: null,
								panel: () => (
									<Suspense fallback={<LazyFallback variant="portal" />}>
										<PartnerGeneral
											orgId={partnerId}
											partnershipId={primaryPartnership.id}
											organizationName={organizationName}
											partnerships={partnerships}
										/>
									</Suspense>
								),
								cyId: "general-tab",
							},
						],
					},
					{
						name: "details",
						panels: [
							{
								label: t("timeline.timeline"),
								errors: null,
								panel: () => (
									<Suspense fallback={<LazyFallback variant="portal" />}>
										<PartnerTimeline
											orgId={partnerId}
											partnershipId={primaryPartnership.id}
										/>
									</Suspense>
								),
								cyId: "timeline-tab",
							},
							{
								label: t("partners.agreements"),
								errors: null,
								panel: () => (
									<Suspense fallback={<LazyFallback variant="portal" />}>
										<PartnerAgreements
											orgId={partnerId}
											partnershipId={primaryPartnership.id}
										/>
									</Suspense>
								),
								cyId: "timeline-tab",
							},
						],
					},
				]}
			/>
		</ContentBox>
	);
}
